import {
  unityContentsService,
  unitySectionService,
} from '@tbx/experience-widgets-lib';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import { convertPath } from '../../../utils/pathRoutesUtils';
import { ZAPPING_PAGE_SIZE } from '../../../utils/zappingUtils';
import * as actions from './actions';
import { types } from './constants';

function* fetchContentWidget(action) {
  const { accessToken, contentID } = action;

  try {
    const { result } = yield call(
      unityContentsService.getContentsById,
      accessToken,
      contentID,
    );

    if (!result || result.error) {
      throw new Error(result.error);
    }

    return yield all([put(actions.loadWidgetSuccess(result))]);
  } catch (e) {
    console.error('SECTION_WIDGET_FETCH_ERROR: ', e);
    yield put(actions.loadWidgetError(e));
  }
}

function* startNextEpisode(action) {
  const { status, router, contentid, pathPlayer } = action;

  try {
    yield put(actions.startNextEpisodeSuccess(status));

    router.push(convertPath(pathPlayer, contentid));
  } catch (e) {
    console.error('SECTION_WIDGET_FETCH_ERROR: ', e);
    yield put(actions.startNextEpisodeError(e));
  }
}

/**
 * Fetches the content list the content was opened from to build the zapping
 * carousel. It's ancillary: if it fails, the player still displays, just
 * without the zapping button.
 */
function* fetchZappingList(action) {
  const { accessToken, zappingOrigin } = action;

  try {
    const { result } = yield call(
      unitySectionService.getSectionComponentItems,
      accessToken,
      zappingOrigin.sectionId,
      zappingOrigin.widgetId,
      { pageSize: ZAPPING_PAGE_SIZE, page: 1 },
      zappingOrigin.itemsURL,
    );

    if (!result || result.error) {
      throw new Error(result?.error);
    }

    // The items endpoint returns the content list in `result`
    const contents = Array.isArray(result) ? result : result.result || [];

    yield put(actions.loadZappingListSuccess(contents));
  } catch (e) {
    console.error('ZAPPING_LIST_FETCH_ERROR: ', e);
    yield put(actions.loadZappingListError(e));
  }
}

/**
 * Fetches the remaining episodes of the season of the series an episode
 * belongs to, to build the zapping list for an EPISODE content. It's
 * ancillary: if it fails, the player still displays, just without the
 * zapping button.
 *
 * The season may have more episodes than ZAPPING_PAGE_SIZE, so it paginates
 * against the service until gathering the total the API reports.
 */
function* fetchZappingEpisodes(action) {
  const { accessToken, serieId, season } = action;
  const key = `${serieId}:${season}`;

  try {
    let episodes = [];
    let page = 1;
    let total = null;

    while (total === null || episodes.length < total) {
      const { result } = yield call(
        unityContentsService.getContentsEpisodes,
        accessToken,
        serieId,
        { contentType: 'SERIE', season, page, pageSize: ZAPPING_PAGE_SIZE },
      );

      if (!result || result.error) {
        throw new Error(result?.error);
      }

      const pageItems = result.result || [];
      total = result.total ?? episodes.length + pageItems.length;
      episodes = episodes.concat(pageItems);

      // Stops if the API returns no more items, even if the total says
      // otherwise, so the saga doesn't wait for a page that never arrives.
      if (!pageItems.length) {
        break;
      }

      page += 1;
    }

    yield put(actions.loadZappingEpisodesSuccess(key, episodes));
  } catch (e) {
    console.error('ZAPPING_EPISODES_FETCH_ERROR: ', e);
    yield put(actions.loadZappingEpisodesError(e));
  }
}

function* saga() {
  yield takeLatest(types.WIDGET_FETCH, fetchContentWidget);
  yield takeLatest(types.START_NEXT_EPISODE, startNextEpisode);
  yield takeLatest(types.ZAPPING_LIST_FETCH, fetchZappingList);
  yield takeLatest(types.ZAPPING_EPISODES_FETCH, fetchZappingEpisodes);
}

export default saga;
